import { Component, Input, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, Observable, of, Subject, zip } from 'rxjs';
import { catchError, debounceTime, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { UserApiService } from 'src/app/modules/management/store/users';
import * as fromAuth from 'src/app/modules/authentication/store/auth';
import * as fromSecurityGroup from 'src/app/modules/management/store/security-groups';

@Component({
    selector: 'topo-team-member-select',
    templateUrl: './team-member-select.component.html',
    styleUrls: ['./team-member-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: TeamMemberSelectComponent
        }
    ],
    standalone: false
})
export class TeamMemberSelectComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() placeholder: string;
  @Input() selectedMembers: any[];
  @Output() selectedMembersChanged = new EventEmitter<any[]>();

  membersInput$ = new Subject<string>();
  destroy$: Subject<void> = new Subject<void>();
  users$ = new BehaviorSubject<any[]>([]);
  groups$ = new BehaviorSubject<any[]>([]);
  groupGroupName: string;
  userGroupName: string;
  membersLoading: boolean;
  members: any[];
  typeToSearchText: string;

  touched = false;
  @Input() disabled = false;
  onChange = value => {};
  onTouched = () => {};

  constructor(
    private store: Store<fromAuth.State>,
    private translateService: TranslateService,
    private userApiService: UserApiService,
    private securityGroupApi: fromSecurityGroup.SecurityGroupApiService
  ) {}

  ngOnInit(): void {
    this.groupGroupName = this.translateService.instant('MANAGEMENT.WORKFLOW.TEAM-MEMBER-TYPE-GROUPS');
    this.userGroupName = this.translateService.instant('MANAGEMENT.WORKFLOW.TEAM-MEMBER-TYPE-USERS');
    this.typeToSearchText = this.translateService.instant('UTILITY.NG-SELECT-PROMPT');

    zip(this.users$, this.groups$)
      .pipe(takeUntil(this.destroy$))
      .subscribe(([users, groups]) => {
        this.membersLoading = false;

        const values = [];

        values.push(
          ...users.map(u => {
            return {
              uId: u.uId,
              displayName: u.displayName,
              type: this.userGroupName
            };
          })
        );

        values.push(
          ...groups.map(d => {
            return {
              uId: d.uId,
              displayName: d.displayName,
              type: this.groupGroupName
            };
          })
        );

        this.members = values;
      });

    this.membersInput$.pipe(debounceTime(400), takeUntil(this.destroy$)).subscribe(val => {
      this.searchForUsersAndGroups(val);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  searchForUsersAndGroups(searchTerm: string): void {
    this.getUsers(searchTerm)
      .pipe(take(1))
      .subscribe(users => {
        this.users$.next(users);
      });

    this.getGroups(searchTerm)
      .pipe(take(1))
      .subscribe(dist => {
        this.groups$.next(dist);
      });
  }

  getUsers(searchTerm: string = null): Observable<any[]> {
    this.membersLoading = true;

    return this.store.select(fromAuth.selectUserContext).pipe(
      take(1),
      switchMap(userContext => {
        return this.userApiService
          .getUsers(userContext.organizationUId, {
            skip: 0,
            take: 20,
            sort: 'DisplayName',
            where: !!searchTerm
              ? `DisplayName.ToLower().Contains("${searchTerm.replace('"', '\\"')?.toLocaleLowerCase()}") AND IsServiceAccount==false`
              : 'IsServiceAccount==false'
          })
          .pipe(
            catchError(() => of({ data: [] })),
            map(({ data }) => {
              return data;
            })
          );
      })
    );
  }

  getGroups(searchTerm: string = null): Observable<any[]> {
    this.membersLoading = true;

    return this.store.select(fromAuth.selectUserContext).pipe(
      take(1),
      switchMap(userContext =>
        this.securityGroupApi
          .getSecurityGroups(userContext.organizationUId, {
            skip: 0,
            take: 100,
            sort: 'DisplayName',
            where: !!searchTerm ? `DisplayName.ToLower().Contains("${searchTerm.replace('"', '"').toLocaleLowerCase()}")` : ''
          })
          .pipe(
            catchError(() => of({ data: [] })),
            map(({ data }) => {
              return data;
            })
          )
      )
    );
  }

  memberChanged(event): void {
    this.selectedMembersChanged.emit(this.selectedMembers);
    this.markAsTouched();
    this.onChange(this.selectedMembers);
  }

  addMember(event): void {
    this.selectedMembersChanged.emit(this.selectedMembers);
    this.markAsTouched();
    this.onChange(this.selectedMembers);
  }

  removeMember(event): void {
    this.selectedMembersChanged.emit(this.selectedMembers);
    this.markAsTouched();
    this.onChange(this.selectedMembers);
  }

  writeValue(selectedMembers: any[]) {
    this.selectedMembers = selectedMembers;
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  trackByFn(val) {
    return val;
  }
}
